import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import Layout from './Layout';
import SerpChecker from './SerpChecker';
import ShareModal from './ShareModal';
import { buildSerpUrl, uuleV1FromCanonical, uuleV2FromLatLng } from '../lib/serpUrl';
import { LONDON, UK_URL, UK_UULE } from '../lib/exampleUrls';

// Guide + embedded tool for the "see Google from another location" intent (A04,
// SEO audit 2026-10-06). Every example URL below is produced by the same
// buildSerpUrl() the tool uses, so the guide cannot drift from the code.
// Honesty invariant (DEC-007 / DEC-009): the tool opens Google's real results
// page with location + language parameters. No rank table, no stored history,
// no device emulation. Do not promise any of that here.

const PAGE_URL = 'https://freeserpchecker.org/google-location-changer/';
const TITLE = 'Google Location Changer: Search Google from Another Location';
const DESCRIPTION =
  'See Google results from any country or city without a VPN. How uule, gl and hl change the results page, with real example URLs you can open.';

// Place-name (v1) uule, shown only to explain the format: Google ignored it in
// every test on 2026-10-06 (LRN-029), so no example link relies on it.
const UK_V1_UULE = uuleV1FromCanonical('United Kingdom');

// Chicago (Grant Park area) as Nominatim returns it. The real tool stamps the
// current time into the city uule, so every city link is slightly different;
// the guide pins one timestamp so the example stays stable.
const CHICAGO = { lat: '41.8755616', lng: '-87.6244212' };
const CHICAGO_TS = 1791302383485000;
const CHICAGO_UULE = uuleV2FromLatLng(parseFloat(CHICAGO.lat), parseFloat(CHICAGO.lng), {
  timestamp: CHICAGO_TS,
});
// Built by the real buildSerpUrl(); only the uule is swapped for the pinned one.
const CHICAGO_URL = (() => {
  const url = new URL(
    buildSerpUrl({
      keyword: 'plumber',
      countryCode: 'US',
      countryLabel: 'United States',
      coords: CHICAGO,
      language: 'en',
      num: 10,
      safe: false,
    })
  );
  url.searchParams.set('uule', CHICAGO_UULE);
  return url.toString();
})();

const CHICAGO_DECODED = `role:1
producer:12
provenance:6
timestamp:${CHICAGO_TS}
latlng{
latitude_e7:418755616
longitude_e7:-876244212
}
radius:93000`;

const PARAMS = [
  ['q', 'running shoes', 'The keyword, exactly as you typed it.'],
  ['hl', 'en', "Google's interface language. It also nudges which language the results come in."],
  ['gl', 'gb', 'The country. On its own it gave us UK shops priced in pounds, but not a place inside the UK.'],
  ['uule', UK_UULE, 'The place, here central London, written as coordinates. This is the format that can pin a page to a place (more below).'],
  ['num', '10', 'One standard page of results.'],
  ['safe', 'off', 'SafeSearch off, so nothing is filtered out of what a normal user sees.'],
  ['pws', '0', 'Asks Google not to personalize the page with your own search history.'],
  ['ll', `${LONDON.lat},${LONDON.lng}`, 'The same coordinates in plain text. The tool adds it to every search with a city.'],
];

const PAGE_FAQ = [
  {
    q: 'Is using a location changer legal?',
    a: "Checking how Google looks in another market is routine SEO and market research. This tool doesn't hide your IP address, fake your device's GPS or run searches automatically: it builds a google.com link with a location in it, and you open it yourself, one search at a time, under Google's normal terms of service. What is legal depends on where you are and what you do with the results, so this is general information, not legal advice.",
  },
  {
    q: 'How do I change my current location on Google?',
    a: "Google estimates your location from your device's location, the home and work addresses saved in your Google account, places you searched before and your IP address. You can't type an arbitrary city into Google's settings. What you can do is add the place to the query (\"plumber in Chicago\"), change the addresses in your account, or turn device location off. To see the results page someone in another place gets, without touching any of those settings, open a link with gl and a coordinate uule in it. That's the link the tool on this page builds.",
  },
  {
    q: 'Why does Google say "Can\'t determine location" at the bottom of the page?',
    a: 'That footer line describes your device location, not the location the results were built for. When we opened a United Kingdom search (gl=gb) on October 6, 2026, the footer said "Can\'t determine location" while the page showed UK shops, prices in pounds and adidas.co.uk. Judge the location by the results themselves: currency, local domains, addresses in the map pack.',
  },
  {
    q: 'Can I see mobile results with a location changer?',
    a: 'Not with URL parameters alone. Google decides between the mobile and desktop page from the browser that opens it, so open the link on your phone, or switch your desktop browser to a mobile device in its developer tools, if you need the mobile page. The location part works the same in both.',
  },
  {
    q: 'Is a location changer the same as a VPN?',
    a: "No. A VPN moves your whole connection to another country, so every site you visit sees that country. A location changer only tells Google where the search should come from. Google's page is localized, but if you click through to a website that adapts to your IP address, that website still sees where you really are.",
  },
];

const CodeBlock = ({ children, label }) => (
  <pre
    aria-label={label}
    className="mt-4 overflow-x-auto rounded border border-slate-200 bg-slate-50 p-4 text-xs sm:text-sm leading-6 text-slate-800 whitespace-pre-wrap break-all"
  >
    <code>{children}</code>
  </pre>
);

const GoogleLocationChangerPage = () => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  return (
    <Layout breadcrumbItems={[{ label: 'Google Location Changer', href: '/google-location-changer/' }]}>
      <Helmet defer={false}>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content="https://freeserpchecker.org/og-image.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={PAGE_URL} />
        <meta property="twitter:title" content={TITLE} />
        <meta property="twitter:description" content={DESCRIPTION} />
        <meta property="twitter:image" content="https://freeserpchecker.org/og-image.png" />
      </Helmet>

      <section className="home-hero">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="home-title">Google Location Changer</h1>
            <p className="mt-3 text-base sm:text-lg leading-7 text-slate-500 max-w-2xl mx-auto">
              Search Google from another country or city. No VPN, no extension, no signup.
            </p>
          </div>
          <section
            id="location-changer"
            aria-label="Search Google from another location"
            className="max-w-2xl mx-auto mt-8 sm:mt-10 scroll-mt-20"
          >
            <SerpChecker onShare={() => setIsShareModalOpen(true)} />
          </section>
          <p className="mt-6 text-center text-sm text-slate-500">
            Pick a country, add a city if you need the local view, and Google opens in a new tab.
          </p>
        </div>
        <ShareModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} />
      </section>

      <article className="home-band home-band-white">
        <div className="container-max max-w-3xl px-4 sm:px-6 lg:px-8 text-slate-700 leading-7">
          <p className="eyebrow">Guide</p>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
            How to see Google results from another location (uule, gl and hl explained)
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            By{' '}
            <a href="https://danielpajuelo.com/sobre-mi/" className="underline underline-offset-2 hover:text-brand-600">
              Daniel Pajuelo
            </a>
            , SEO consultant and maintainer of this tool. Examples tested on October 6, 2026.
          </p>

          <p className="mt-6">
            Google doesn't have one results page per keyword. It builds a page for a place: the same search for
            "plumber" lists different companies in Chicago and in Houston, and "running shoes" shows prices in
            pounds in the UK and in dollars in the US. If you do SEO for a market you aren't in, the page you see at
            your desk is the wrong one. You can travel, rent a VPN, or tell Google where the search comes from. This
            tool does the last one, and the rest of this guide shows exactly how.
          </p>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">What the tool sends to Google</h3>
          <p className="mt-3">
            Search "running shoes" with the country set to United Kingdom, the city set to London and the language
            set to English, and the tool opens this link:
          </p>
          <CodeBlock label="Google URL for running shoes in London">{UK_URL}</CodeBlock>
          <p className="mt-3">
            <a href={UK_URL} target="_blank" rel="noopener noreferrer nofollow" className="text-brand-600 hover:text-brand-700 font-semibold underline underline-offset-2">
              Open this example on Google
            </a>
            . It's a normal google.com search with eight parameters:
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-300">
                  <th scope="col" className="py-2 pr-4 font-semibold text-slate-900">Parameter</th>
                  <th scope="col" className="py-2 pr-4 font-semibold text-slate-900">Value</th>
                  <th scope="col" className="py-2 font-semibold text-slate-900">What it does</th>
                </tr>
              </thead>
              <tbody>
                {PARAMS.map(([name, value, what]) => (
                  <tr key={name} className="border-b border-slate-200 align-top">
                    <td className="py-2 pr-4 font-mono">{name}</td>
                    <td className="py-2 pr-4 font-mono break-all">{value}</td>
                    <td className="py-2">{what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            The country part is the easy one. Earlier on October 6, 2026, a "running shoes" link with{' '}
            <span className="font-mono">gl=gb</span> and no working uule already returned adidas.co.uk, Runners
            Need, Decathlon UK and Sports Direct, with shopping results priced in pounds, from a connection in Spain.
          </p>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">gl, hl and uule: which one does what</h3>
          <p className="mt-3">
            <span className="font-mono">gl</span> and <span className="font-mono">hl</span> are documented. In{' '}
            <a
              href="https://developers.google.com/custom-search/v1/reference/rest/v1/cse/list"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-brand-600"
            >
              Google's Custom Search API reference
            </a>
            , <span className="font-mono">gl</span> is a two-letter country code that boosts results from that
            country, and <span className="font-mono">hl</span> sets the interface language. In practice{' '}
            <span className="font-mono">gl</span> is more than a boost: we searched "weather" with{' '}
            <span className="font-mono">gl=tr</span> and no uule, and the page came back localized to Turkey. What
            it doesn't give you is a place. We searched "plumber" with{' '}
            <span className="font-mono">gl=us</span> and no uule from a connection in Spain: Google answered with
            American results, but the map pack mixed plumbers from Arizona, Utah and Oklahoma. That page doesn't
            match what anyone in a real US city sees.
          </p>
          <p className="mt-3">
            <span className="font-mono">uule</span> is the parameter that pins the search to a place. Google
            doesn't document it publicly. It comes in two formats, and in our tests only one of them did anything.
          </p>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">The uule built from a place name: Google ignored it</h3>
          <p className="mt-3">
            One format carries a place name. For the United Kingdom it is{' '}
            <span className="font-mono break-all">{UK_V1_UULE}</span>. Drop the <span className="font-mono">w+</span>{' '}
            prefix, decode the rest from base64, and you get six bytes of header followed by the name in plain text:
          </p>
          <CodeBlock label="Decoded place-name uule">{`08 02 10 20 22 0e | United Kingdom`}</CodeBlock>
          <p className="mt-3">
            It's a tiny protobuf message. <span className="font-mono">08 02</span> and{' '}
            <span className="font-mono">10 20</span> are two numeric fields with fixed values (2 and 32),{' '}
            <span className="font-mono">22</span> opens the text field and <span className="font-mono">0e</span> is
            its length in bytes: "United Kingdom" is 14 bytes, 0e in hexadecimal.
          </p>
          <p className="mt-3">
            To see what it does on its own, we searched "weather" without <span className="font-mono">gl</span>,
            with this kind of uule for United Kingdom, Eswatini, Swaziland, "London,England,United Kingdom" and a
            country that doesn't exist. All five gave the same page: the weather for the town our IP address is in,
            near Valencia, Spain. The names were spelled as in Google Ads' own list of locations, so spelling wasn't
            the problem. Google simply ignored them. With a place-name uule, only{' '}
            <span className="font-mono">gl</span> localizes a search by country.
          </p>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">The uule built from coordinates: the one that works</h3>
          <p className="mt-3">
            The same "weather" test with a coordinate uule for Mbabane, the capital of Eswatini, showed the weather
            for Mbabane. That is the format of the London example above. Without{' '}
            <span className="font-mono">gl</span> it isn't reliable: the London and Prague coordinates still showed
            the weather near Valencia. With a matching <span className="font-mono">gl</span> it worked in every
            case we tried: <span className="font-mono">gl=cz</span> with Prague showed Prague,{' '}
            <span className="font-mono">gl=au</span> with Canberra showed Canberra and{' '}
            <span className="font-mono">gl=sz</span> with Mbabane showed Mbabane. That's why the tool always sends
            gl along with the uule. Still, check the page you get; the FAQ below explains what to look at. The format matters most
            for "plumber", "dentist" or
            anything with a map pack, because Google ranks local businesses by distance from the searcher. When you
            add a city, the tool looks it up in OpenStreetMap, gets its coordinates and writes them into the uule.
            For Chicago:
          </p>
          <CodeBlock label="Google URL for plumber in Chicago">{CHICAGO_URL}</CodeBlock>
          <p className="mt-3">This uule starts with "a " and decodes to readable text:</p>
          <CodeBlock label="Decoded city uule">{CHICAGO_DECODED}</CodeBlock>
          <p className="mt-3">
            Latitude and longitude are stored multiplied by ten million, without decimals. The timestamp is the
            current time in microseconds, so the tool produces a slightly different link every time you search the
            same city. The tool also adds an <span className="font-mono">ll</span> parameter with the same
            coordinates. With this link, the query "plumber", with no city in it, returned Chicago plumbers, a map
            pack around downtown Chicago and the question "Who is the best plumber in Chicago?".
          </p>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">Country or city: which one to pick</h3>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <li>
              <strong>Country only</strong> for national queries: products, brands, information. You get a
              country-level page, close to what a signed-out user in that country sees for non-local results. The
              country comes from <span className="font-mono">gl</span>, and the tool adds a coordinate uule for the
              capital, so any local results lean toward the capital.
            </li>
            <li>
              <strong>City</strong> for anything a business near the searcher can answer: services, restaurants,
              shops, "near me" queries. Two cities in the same country can show completely different map packs.
            </li>
            <li>
              <strong>Language</strong> separately, when the market is bilingual: English results in Spain, Spanish
              results in the United States, French results in Canada.
            </li>
          </ul>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">What a location changer can't change</h3>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <li>
              <strong>Device.</strong> Mobile or desktop depends on the browser that opens the link, not on a
              parameter.
            </li>
            <li>
              <strong>Your account.</strong> If you are signed in to Google, your history can still shape the page.
              Use a private window for the cleanest view.
            </li>
            <li>
              <strong>Websites you click through to.</strong> They still see your real IP address and country.
            </li>
            <li>
              <strong>Ranking positions over time.</strong> The tool opens one live page and keeps nothing. You read
              positions off Google yourself; for daily history you need a rank tracker.
            </li>
            <li>
              <strong>Google's rate limits.</strong> The searches come from your connection, so many searches in a
              row can trigger a CAPTCHA from Google.
            </li>
          </ul>

          <h3 className="mt-10 text-xl font-semibold text-slate-900">Questions people ask</h3>
          <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {PAGE_FAQ.map((item) => (
              <details key={item.q} className="faq-row">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>

          <p className="mt-10">
            <a href="#location-changer" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-semibold">
              Try it with your own keyword <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </a>
          </p>
          <p className="mt-2 text-sm">
            Step-by-step instructions for the form are in{' '}
            <a href="/how-to-use/" className="underline underline-offset-2 hover:text-brand-600">How to use</a>; the{' '}
            <a href="/faq/" className="underline underline-offset-2 hover:text-brand-600">FAQ</a> covers accuracy,
            privacy and pricing.
          </p>
        </div>
      </article>
    </Layout>
  );
};

export default GoogleLocationChangerPage;
